import React, { useState, useMemo } from 'react'
import { Container, Grid, Typography, Box, InputAdornment, TextField, MenuItem, Select, FormControl, InputLabel, Paper, Stack, Chip, Fade, Button } from '@mui/material'
import { Search, AutoAwesome, KeyboardArrowDown } from '@mui/icons-material'
import PoemCard from '../../features/poems/components/PoemCard'
import { useSelector, useDispatch } from 'react-redux'
import type { RootState } from '../../store/store'
import { useTranslation } from 'react-i18next'
import SEOHead from '../../shared/components/SEOHead'
import AdSenseManager from '../../shared/components/AdSenseManager'
import DecorativeTitle from '../../shared/components/DecorativeTitle'
import decoratorHome from '../../assets/decorator_home.png'
import { useGetPoemsQuery } from '../../store/apiSlice'
import { useNavigate } from 'react-router-dom'

// Inline ErrorStateCard for safety if not extracted
import { ErrorOutline } from '@mui/icons-material'
import { Card, CardContent } from '@mui/material'

const ErrorStateCardInline = React.forwardRef<HTMLDivElement, {
  title: string
  message: string
  retryLabel?: string
  onRetry?: () => void
}>(({ title, message, retryLabel, onRetry }, ref) => (
  <Card
    ref={ref}
    elevation={0}
    sx={(theme) => ({
      borderRadius: 3,
      border: `1px solid ${theme.palette.error.light}33`,
      background:
        theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, rgba(239,83,80,0.18), rgba(229,57,53,0.28))'
          : 'linear-gradient(135deg, rgba(255,205,210,0.35), rgba(239,83,80,0.25))'
    })}
  >
    <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Box
        sx={(theme) => ({
          color: theme.palette.error.main,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        })}
      >
        <ErrorOutline fontSize="large" />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h6" sx={{ color: 'error.main', mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.primary' }}>
          {message}
        </Typography>
        {retryLabel && onRetry && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            sx={{ mt: 2 }}
            onClick={onRetry}
          >
            {retryLabel}
          </Button>
        )}
      </Box>
    </CardContent>
  </Card>
))
ErrorStateCardInline.displayName = 'ErrorStateCard'

const Home: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { data: poems, isLoading: loading, error: fetchError, refetch } = useGetPoemsQuery()
  const error = fetchError ? 'Failed to fetch poems' : null

  // Ensure items is an array
  const safePoems = Array.isArray(poems) ? poems : []

  const [searchTerm, setSearchTerm] = useState('')
  const [filter, setFilter] = useState('all') // 'all' or category
  const [sortBy, setSortBy] = useState('date')

  // SEO Description
  const metaDescription = "Entdecken Sie die Schönheit tamilischer Gedichte. Eine Sammlung voller Emotionen, Kultur und zeitloser Weisheit für Ihre Seele."

  // Categories
  const categories = React.useMemo(() => {
    return [...new Set(safePoems.map(p => p.category).filter(Boolean))]
  }, [safePoems])

  // Filter Logic
  const filteredPoems = useMemo(() => {
    let result = [...safePoems]

    // Search
    if (searchTerm) {
      const lower = searchTerm.toLowerCase()
      result = result.filter(p =>
        p.title.toLowerCase().includes(lower) ||
        p.content.toLowerCase().includes(lower) ||
        (p.author && p.author.toLowerCase().includes(lower))
      )
    }

    // Category Filter
    if (filter !== 'all') {
      result = result.filter(p => p.category === filter)
    }

    // Sort
    result.sort((a, b) => {
      if (sortBy === 'popular') {
        return (b.viewCount || 0) - (a.viewCount || 0)
      } else if (sortBy === 'title') {
        return a.title.localeCompare(b.title)
      } else if (sortBy === 'author') {
        return (a.author || '').localeCompare(b.author || '')
      } else {
        // Date (default)
        return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
      }
    })

    return result
  }, [safePoems, searchTerm, filter, sortBy])

  const displayedError = error

  const handleRetry = () => {
    refetch()
  }

  return (
    <>
      <SEOHead
        title="Kavithai - Tamil Poems for the Soul"
        description={metaDescription}
        keywords={['Tamil Poems', 'Kavithaigal', 'Literature', 'Poetry', 'Tamil Culture']}
      />



      <Container sx={{ py: 4 }} id="main-content">
        <Grid container spacing={4}>
          {/* Left Sidebar Ad */}
          <Grid item xs={12} md={2} sx={{ display: { xs: 'none', md: 'block' } }}>
            <AdSenseManager page="home" position="sidebar-left" />
          </Grid>

          {/* Main Content Area */}
          <Grid item xs={12} md={8}>
            <Fade in timeout={800}>
              <Box sx={{ mb: 6 }} id="all-poems">
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
                  <Typography variant="h4" component="h1" sx={{ fontWeight: 700, fontFamily: '"Noto Serif", serif' }}>
                    {t('allPoems', 'Alle Gedichte')}
                  </Typography>
                </Stack>

                {/* Filters */}
                <Paper elevation={0} sx={{ p: 2, mb: 4, borderRadius: 2, border: (theme) => `1px solid ${theme.palette.divider}` }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      size="small"
                      placeholder={t('searchPlaceholder', 'Suche nach Gedichten...')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ flexGrow: 1 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel id="poem-category-label">Kategorie</InputLabel>
                      <Select
                        labelId="poem-category-label"
                        value={filter}
                        label="Kategorie"
                        onChange={(e) => setFilter(e.target.value)}
                      >
                        <MenuItem value="all">Alle</MenuItem>
                        {categories.map(cat => (
                          <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel id="poem-sort-label">Sortierung</InputLabel>
                      <Select
                        labelId="poem-sort-label"
                        value={sortBy}
                        label="Sortierung"
                        onChange={(e) => setSortBy(e.target.value)}
                      >
                        <MenuItem value="date">Neueste</MenuItem>
                        <MenuItem value="popular">Beliebteste</MenuItem>
                        <MenuItem value="title">Titel (A-Z)</MenuItem>
                        <MenuItem value="author">Autor (A-Z)</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>

                  {(searchTerm || filter !== 'all') && (
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
                      {searchTerm && <Chip label={`Suche: ${searchTerm}`} onDelete={() => setSearchTerm('')} size="small" />}
                      {filter !== 'all' && <Chip label={`Kategorie: ${filter}`} onDelete={() => setFilter('all')} size="small" />}
                    </Stack>
                  )}
                </Paper>

                {/* Error State */}
                {displayedError && (
                  <Box sx={{ mb: 4 }}>
                    <ErrorStateCardInline
                      title={t('error')}
                      message={displayedError}
                      retryLabel={t('retry')}
                      onRetry={handleRetry}
                    />
                  </Box>
                )}

                {/* Loading State */}
                {loading && (
                  <Typography textAlign="center" color="text.secondary" sx={{ py: 4 }}>
                    {t('loading', 'Lade Gedichte...')}
                  </Typography>
                )}

                {/* Empty State */}
                {!loading && !error && filteredPoems.length === 0 && (
                  <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed' }}>
                    <Typography color="text.secondary">
                      Keine Gedichte gefunden.
                    </Typography>
                  </Paper>
                )}

                {/* Poem List - Grid Layout */}
                <Grid container spacing={3}>
                  {filteredPoems.map((poem, index) => (
                    <Grid item xs={12} sm={6} md={6} key={poem.id}>
                      <Fade in timeout={500 + (index * 100)}>
                        <Box sx={{ height: '100%' }}>
                          <PoemCard poem={poem} onClick={() => navigate(`/poem/${poem.id}`)} />
                        </Box>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Fade>
          </Grid>

          {/* Right Sidebar Ad */}
          <Grid item xs={12} md={2} sx={{ display: { xs: 'none', md: 'block' } }}>
            <AdSenseManager page="home" position="sidebar-right" />
          </Grid>
        </Grid>
      </Container>
    </>
  )
}

export default Home
